import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { HiOutlineTableCells } from 'react-icons/hi2';
import './ProfileMenu.css';

interface ProfileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
}

/* Profile popup menu — administrator-only entry point into the Data Mappings admin area. */
export default function ProfileMenu({ isOpen, onClose, anchorRef }: ProfileMenuProps) {
  const navigate = useNavigate();
  const [position, setPosition] = useState<{ left: number; bottom: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isOpen || !anchorRef.current) {
      setPosition(null);
      return;
    }
    const updatePosition = () => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({ left: rect.left, bottom: window.innerHeight - rect.top + 10 });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, anchorRef]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen || !position) return null;

  return ReactDOM.createPortal(
    <div className="profile-popup-menu" ref={menuRef} style={{ left: position.left, bottom: position.bottom }}>
      <div
        className="profile-popup-item"
        onClick={() => {
          onClose();
          navigate('/dashboard/data-mappings');
        }}
      >
        <HiOutlineTableCells />
        <span>Data Mappings</span>
      </div>
    </div>,
    document.body
  );
}
